/**
 * Разбор бэкендового конверта ошибок `{ error: "текст" }`
 * (`internal_handler_http.ErrorResponse` из спеки).
 *
 * Обработка ошибок по-прежнему делегирована react-query — здесь только типы и
 * чистые хелперы разбора, интерсепторы `http` не меняются.
 */
import type { components } from './schema';

/** Тело ошибки от бэкенда: `{ error: "текст" }`. */
export type ApiErrorBody =
  components['schemas']['internal_handler_http.ErrorResponse'];

type AxiosLikeError = {
  response?: { status?: number; data?: unknown };
  message?: string;
};

function asAxiosLike(error: unknown): AxiosLikeError | undefined {
  return typeof error === 'object' && error !== null
    ? (error as AxiosLikeError)
    : undefined;
}

/** Ошибка пришла от бэкенда в формате `{ error }`. */
export function isApiError(error: unknown): boolean {
  const data = asAxiosLike(error)?.response?.data;
  return (
    typeof data === 'object' &&
    data !== null &&
    typeof (data as ApiErrorBody).error === 'string'
  );
}

/** HTTP-код ответа, если он есть. */
export function getApiErrorStatus(error: unknown): number | undefined {
  return asAxiosLike(error)?.response?.status;
}

/**
 * Текст ошибки от бэкенда, иначе `fallback`.
 * Заменяет россыпь `e?.response?.data?.error || 'сообщение'` по коду.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const data = asAxiosLike(error)?.response?.data;
  const message =
    typeof data === 'object' && data !== null
      ? (data as ApiErrorBody).error
      : undefined;

  return typeof message === 'string' && message.trim() ? message : fallback;
}

/** 409 — конфликт версий (оптимистическая блокировка по `version`). */
export function isVersionConflict(error: unknown): boolean {
  return getApiErrorStatus(error) === 409;
}
